import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";

// flatpickr
import "flatpickr/dist/themes/material_blue.css";
import FlatPickr from "react-flatpickr";

import TableContainer from "../tables/TableContainer";
import "../tables/tableCustom.css";

// Custom Axios
import axiosInstance from 'utils/axiosInstance';
import { format, subHours, parse, isSameDay } from 'date-fns';

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb";
import Spinners from "components/Common/Spinner";
import { handleSearchData } from "components/Common/searchFile";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';

const PointRecords = ({ t }) => {

  //meta title
  document.title = "點數紀錄 | TheChart.AI";

  /* ---------------- state ---------------- */
  const [activeTab, setActiveTab] = useState("1");
  const [startDate, setStartDate] = useState(new Date('06-01-2024'));
  
  // -
  const [orders, setOrders] = useState(null);
  const [order, setOrder] = useState(null);
  const [packageOrder, setPackageOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('all');
  const [paymentStatus, setPaymentStatus] = useState('all');
  // -

  // tab 1 – redemptions
  const [redemptionList, setRedemptionList] = useState([]);
  const [redemptionFilter, setRedemptionFilter] = useState({
    date: null,
    status: "all",
  });

  // tab 2 – history
  const [historyList, setHistoryList] = useState([]);
  const [historyFilter, setHistoryFilter] = useState({
    date: null,
    type: "all",
  });

  const [isLoading, setLoading] = useState(false);


  /* ---------------- data fetch ---------------- */

  const loadRedemptions = async () => {
    const res = await axiosInstance.get("/knovia/myInfo/point-redemption-history");
    // adjust time and flatten fields like the reference component
    const data = res.data.map(r => {
      const localDate = subHours(new Date(r.requestedAt), 8);
      return {
        ...r,
        RequestedAt: format(localDate, "yyyy-MM-dd HH:mm:ss"),
      };
    }).sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt));
    console.log('Got redemption list:', data);
    setRedemptionList(data);
  };

  const loadHistory = async () => {
    const res = await axiosInstance.get("/knovia/myInfo/point-history");
    const data = res.data.map(h => {
      const localDate = subHours(new Date(h.date), 8);
      return {
        ...h,
        DateFormatted: format(localDate, "yyyy-MM-dd HH:mm:ss"),
      };
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
    console.log('Got point history list:', data);
    setHistoryList(data);
  };

  useEffect(() => {
    (async () => {
      await Promise.all([loadRedemptions(), loadHistory()]);
      setLoading(false);
    })();
  }, []);


  /* ---------------- tables 2 ---------------- */
  const columnsRedemption = useMemo(() => ([
    { header: "申請時間", accessorKey: "RequestedAt", enableColumnFilter: false, enableSorting: true },
    { header: "幣別", accessorKey: "currency", enableColumnFilter: false, enableSorting: true },
    { header: "點數", accessorKey: "points", enableColumnFilter: false, enableSorting: true },
    /*{ header: "匯率", accessorKey: "exchangeRate", enableColumnFilter: false, enableSorting: true },*/
    {
      header: "轉換比例", 
      accessorKey: "exchangeRateAtRequest",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => {
        const exchangeRatio = row.original.exchangeRateAtRequest;
        return <p>{`${(30/exchangeRatio).toFixed(2)}`+`->1`}</p>;
      }
    },
    { header: "金額", accessorKey: "moneyAmount", enableColumnFilter: false, enableSorting: true },
    {
      header: "狀態", 
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => {
        const map = { pending: "warning", completed: "success", canceled: "danger" };
        return <Badge className={`bg-${map[row.original.status]} font-size-10`}>
          {row.original.status}
        </Badge>;
      }
    },
  ]), []);

  const columnsHistory = useMemo(() => ([
    { header: "時間", accessorKey: "DateFormatted", enableColumnFilter: false, enableSorting: true },
    { header: "類型", accessorKey: "action", enableColumnFilter: false, enableSorting: true },
    { header: "說明", accessorKey: "description", enableColumnFilter: false, enableSorting: true },
    { header: "點數前", accessorKey: "pointsBefore", enableColumnFilter: false, enableSorting: true },
    { header: "變動", accessorKey: "delta", enableColumnFilter: false, enableSorting: true },
    { header: "點數後", accessorKey: "pointsAfter", enableColumnFilter: false, enableSorting: true },
  ]), []);



  /* ---------------- filters ---------------- */

  const filterByDate = (list, selected) =>
    selected
      ? list.filter(i => isSameDay(parse(i.RequestedAt ?? i.DateFormatted,
          "yyyy-MM-dd HH:mm:ss", new Date()), selected))
      : list;

  const filteredRedemptions = useMemo(() => {
    let list = filterByDate(redemptionList, redemptionFilter.date);
    if (redemptionFilter.status !== "all") {
      list = list.filter(r => r.status === redemptionFilter.status);
    }
    return list;
  }, [redemptionList, redemptionFilter]);

  const filteredHistory = useMemo(() => {
    let list = filterByDate(historyList, historyFilter.date);
    if (historyFilter.type !== "all") {
      list = list.filter(h => h.type === historyFilter.type);
    }
    return list;
  }, [historyList, historyFilter]);



  /* ---------------- Tab ---------------- */
  const toggleTab = tab => {
    if (activeTab !== tab) { setActiveTab(tab);}
  };


  if (isLoading) return <Spinners setLoading={setLoading} />;
  

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="點數" breadcrumbItem="紀錄" />

          <Card>
            <CardBody>
              {/*<h4 className="card-title mb-3">點數紀錄</h4>*/}

              <Row>
                <Col xs="10">
                  <ul className="nav nav-tabs nav-tabs-custom" role="tablist">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => { toggleTab("1"); }}
                      >
                        點數兌換紀錄
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "2" })}
                        onClick={() => { toggleTab("2"); }}
                      >
                        點數變動紀錄
                      </NavLink>
                    </NavItem>
                  </ul>
                </Col>

                <Col xs="2"
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    margin: '0vh 0vw 3vh 0vw',
                    paddingRight: '4vw'
                  }}
                > 
                  <div style={{ paddingBottom: '2px' }}>
                    <Button color="primary" 
                      onClick={() => {
                        setLoading(true);
                        fetchOrders();
                        Promise.all([loadRedemptions(), loadHistory()])
                          .finally(() => setLoading(false));
                      }}
                    >
                      <FontAwesomeIcon icon={faSyncAlt} /> {/* Refresh icon */}
                    </Button>
                  </div>
                </Col>
              </Row>

              <TabContent activeTab={activeTab} className="p-3">

                {/* 兌換紀錄 */}
                <TabPane tabId="1">
                  <Form>
                    <Row className="mb-4">
                      <Col md={4}>
                        <FormGroup>
                          <Label>兌換日期</Label>
                          <FlatPickr
                            className="form-control"
                            placeholder="選擇日期"
                            value={redemptionFilter.date}
                            onChange={date => setRedemptionFilter(f => ({ ...f, date }))}
                            options={{ dateFormat: "d M, Y" }}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>狀態</Label>
                          <select
                            className="form-control"
                            value={redemptionFilter.status}
                            onChange={e =>
                              setRedemptionFilter(f => ({ ...f, status: e.target.value }))
                            }
                          >
                            <option value="all">All</option>
                            <option value="pending">pending</option>
                            <option value="completed">completed</option>
                            <option value="canceled">canceled</option>
                          </select>
                        </FormGroup>
                      </Col>

                      <Col sm={12} className="col-xl align-self-end"
                        style={{ display:"flex" }}
                      >
                        <div className="mb-3" style={{ marginRight:'0.8vw' }}>
                          <Button type="button" color="primary" className="w-md">
                            篩選
                          </Button>
                        </div>
                        <div className="mb-3">
                          <Button type="button" color="warning" className="w-md"
                            onClick={()=>{setRedemptionFilter({ date: null, status: "all" });}}
                          >
                            重設條件
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>

                  <TableContainer
                    columns={columnsRedemption}
                    data={filteredRedemptions || []}
                    isCustomPageSize={true}
                    isPagination={true}
                    isGlobalFilter={true}
                    pagination="pagination"
                    SearchPlaceholder={`${filteredRedemptions.length} 項兌換紀錄..`}
                    paginationWrapper="dataTables_paginate paging_simple_numbers"
                    tableClass="table-hover table-nowrap datatable dt-responsive nowrap dataTable no-footer dtr-inline"
                  />
                </TabPane>

                {/* 變動紀錄 */}
                <TabPane tabId="2">
                  <Form>
                    <Row className="mb-4">
                      <Col md={4}>
                        <FormGroup>
                          <Label>日期</Label>
                          <FlatPickr
                            className="form-control"
                            value={historyFilter.date}
                            placeholder="選擇日期"
                            onChange={d => setHistoryFilter(f => ({ ...f, date: d }))}
                            options={{ dateFormat: "d M, Y" }}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>類型</Label>
                          <select
                            className="form-control"
                            value={historyFilter.type}
                            onChange={e =>
                              setHistoryFilter(f => ({ ...f, type: e.target.value }))
                            }
                          >
                            <option value="all">All</option>
                            <option value="earn">earn</option>
                            <option value="spend">spend</option>
                            <option value="redeem_pending">redeem_pending</option>
                            <option value="redeem_cancel">redeem_cancel</option>
                            <option value="redeem_completed">redeem_completed</option>
                            <option value="adjustment">adjustment</option>
                          </select>
                        </FormGroup>
                      </Col>
                      <Col sm={12} className="col-xl align-self-end"
                        style={{ display:"flex" }}
                      >
                        <div className="mb-3" style={{ marginRight:'0.8vw' }}>
                          <Button type="button" color="primary" className="w-md">
                            篩選
                          </Button>
                        </div>
                        <div className="mb-3">
                          <Button type="button" color="warning" className="w-md"
                            onClick={()=>{setRedemptionFilter({ date: null, status: "all" });}}
                          >
                            重設條件
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>

                  <TableContainer
                    columns={columnsHistory}
                    data={filteredHistory || []}
                    isCustomPageSize={true}
                    isPagination={true}
                    isGlobalFilter={true}
                    pagination="pagination"
                    SearchPlaceholder={`${filteredHistory.length} 項變動紀錄..`}
                    paginationWrapper="dataTables_paginate paging_simple_numbers"
                    tableClass="table-hover table-nowrap datatable dt-responsive nowrap dataTable no-footer dtr-inline"
                  />
                </TabPane>
              
              </TabContent>

            </CardBody>
          </Card>

        </Container>
      </div>
    </React.Fragment>
  );
};

PointRecords.propTypes = {};

export default PointRecords;


